import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import PhoneInput from "../components/ui/PhoneInput";
import { formatPhone } from "../utils/phoneUtils";
import { LoadingCard } from "../components/LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  Palette,
  DollarSign,
  Eye,
  Edit,
  ExternalLink,
  Trash2,
} from "lucide-react";
import { designerService } from "../services/designerService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";

export default function Designers() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedDesigner, setSelectedDesigner] = useState(null);
  const [editDesigner, setEditDesigner] = useState(null);
  const [newDesigner, setNewDesigner] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    specialty: "",
    rate_per_hour: "",
    his_license_number: "",
    portfolio_url: "",
    notes: ""
  });
  const [deleteClicks, setDeleteClicks] = useState({});
  
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // API hooks
  const { data: designersData, isLoading: loading } = useQuery({
    queryKey: ['designers', searchTerm],
    queryFn: () => designerService.getAll({ search: searchTerm || undefined })
  });
  
  const createDesignerMutation = useMutation({
    mutationFn: (designerData) => designerService.create(designerData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designers'] });
      toast({ description: "Designer created successfully!" });
      setNewDesigner({
        name: "",
        email: "",
        phone: "",
        address: "",
        specialty: "",
        rate_per_hour: "",
        his_license_number: "",
        portfolio_url: "",
        notes: ""
      });
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to create designer"
      });
    }
  });

  const updateDesignerMutation = useMutation({
    mutationFn: ({ id, data }) => designerService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designers'] });
      toast({ description: "Designer updated successfully!" });
      setIsEditDialogOpen(false);
      setEditDesigner(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to update designer"
      });
    }
  });

  const designers = designersData?.data || [];

  const handleAddDesigner = async () => {
    const designerData = {
      ...newDesigner,
      rate_per_hour: newDesigner.rate_per_hour ? parseFloat(newDesigner.rate_per_hour) : undefined,
      email: newDesigner.email || undefined,
      phone: newDesigner.phone || undefined,
      address: newDesigner.address || undefined,
      specialty: newDesigner.specialty || undefined,
      his_license_number: newDesigner.his_license_number || undefined,
      portfolio_url: newDesigner.portfolio_url || undefined,
      notes: newDesigner.notes || undefined
    };
    
    createDesignerMutation.mutate(designerData);
  };

  const handleViewDesigner = (designer) => {
    setSelectedDesigner(designer);
    setIsViewDialogOpen(true);
  };

  const handleEditDesigner = (designer) => {
    setEditDesigner({
      ...designer,
      rate_per_hour: designer.rate_per_hour?.toString() || "",
      his_license_number: designer.his_license_number || ""
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateDesigner = async () => {
    if (!editDesigner) return;
    
    const designerId = editDesigner.id || editDesigner._id;
    const updateData = {
      ...editDesigner,
      rate_per_hour: parseFloat(editDesigner.rate_per_hour) || null
    };
    
    // Remove MongoDB _id from update data
    delete updateData._id;
    
    updateDesignerMutation.mutate({
      id: designerId,
      data: updateData
    });
  };

  const handleDeleteDesigner = async (designer) => {
    const designerId = designer.id || designer._id;
    const currentClicks = deleteClicks[designerId] || 0;
    
    if (currentClicks === 0) {
      console.warn(`⚠️ DELETE WARNING: Click delete again to confirm deletion of "${designer.name}"`);
      setDeleteClicks(prev => ({ ...prev, [designerId]: 1 }));
      setTimeout(() => {
        setDeleteClicks(prev => ({ ...prev, [designerId]: 0 }));
      }, 3000);
      return;
    }
    
    try {
      console.log(`🗑️ Deleting designer: ${designer.name}`);
      await designerService.delete(designerId);
      window.location.reload();
    } catch (error) {
      console.error("❌ Failed to delete designer:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Designers</h1>
          <p className="text-gray-600 mt-1">Manage your network of landscape designers and architects</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand-600 hover:bg-brand-700 w-full sm:w-auto shrink-0">
              <Plus className="mr-2 h-4 w-4" />
              Add Designer
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Designer</DialogTitle>
              <DialogDescription>
                Add a new designer to your network.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={newDesigner.name}
                  onChange={(e) => setNewDesigner({...newDesigner, name: e.target.value})}
                  placeholder="Sarah Garden Designs"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newDesigner.email}
                  onChange={(e) => setNewDesigner({...newDesigner, email: e.target.value})}
                  placeholder="sarah@gardendesigns.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <PhoneInput
                  id="phone"
                  value={newDesigner.phone}
                  onChange={(val) => setNewDesigner({...newDesigner, phone: val})}
                  placeholder="916.232.2332"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="specialty">Specialty</Label>
                <Select onValueChange={(value) => setNewDesigner({...newDesigner, specialty: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="garden_design">Garden Design</SelectItem>
                    <SelectItem value="hardscape_design">Hardscape Design</SelectItem>
                    <SelectItem value="landscape_architecture">Landscape Architecture</SelectItem>
                    <SelectItem value="sustainable_design">Sustainable Design</SelectItem>
                    <SelectItem value="commercial_design">Commercial Design</SelectItem>
                    <SelectItem value="residential_design">Residential Design</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rate">Hourly Rate</Label>
                <Input
                  id="rate"
                  type="number"
                  value={newDesigner.rate_per_hour}
                  onChange={(e) => setNewDesigner({...newDesigner, rate_per_hour: e.target.value})}
                  placeholder="150"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="his_license">HIS License Number</Label>
                <Input
                  id="his_license"
                  value={newDesigner.his_license_number}
                  onChange={(e) => setNewDesigner({...newDesigner, his_license_number: e.target.value})}
                  placeholder="HIS-12345"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="portfolio">Portfolio URL</Label>
                <Input
                  id="portfolio"
                  value={newDesigner.portfolio_url}
                  onChange={(e) => setNewDesigner({...newDesigner, portfolio_url: e.target.value})}
                  placeholder="https://portfolio.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={newDesigner.address}
                  onChange={(e) => setNewDesigner({...newDesigner, address: e.target.value})}
                  placeholder="123 Design St, City, State"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newDesigner.notes}
                  onChange={(e) => setNewDesigner({...newDesigner, notes: e.target.value})}
                  placeholder="Additional notes about this designer..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddDesigner}
                disabled={createDesignerMutation.isPending || !newDesigner.name}
                className="bg-brand-600 hover:bg-brand-700"
              >
                {createDesignerMutation.isPending ? "Adding..." : "Add Designer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* View Designer Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Designer Details</DialogTitle>
            <DialogDescription>
              View information about {selectedDesigner?.name}
            </DialogDescription>
          </DialogHeader>
          {selectedDesigner && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Name</Label>
                  <p className="text-sm text-gray-600">{selectedDesigner.name}</p>
                </div>
                <div>
                  <Label className="font-semibold">Specialty</Label>
                  <p className="text-sm text-gray-600">
                    {selectedDesigner.specialty ? 
                      selectedDesigner.specialty.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                      : 'Not specified'
                    }
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Email</Label>
                  <p className="text-sm text-gray-600">{selectedDesigner.email || 'Not provided'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Phone</Label>
                  <p className="text-sm text-gray-600">{formatPhone(selectedDesigner.phone) || 'Not provided'}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Hourly Rate</Label>
                  <p className="text-sm text-gray-600">
                    {selectedDesigner.rate_per_hour ? `$${selectedDesigner.rate_per_hour}/hr` : 'Not set'}
                  </p>
                </div>
                <div>
                  <Label className="font-semibold">Status</Label>
                  <Badge className={selectedDesigner.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}>
                    {selectedDesigner.status || 'active'}
                  </Badge>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">HIS License Number</Label>
                  <p className="text-sm text-gray-600">{selectedDesigner.his_license_number || 'Not provided'}</p>
                </div>
              </div>
              <div>
                <Label className="font-semibold">Portfolio</Label>
                {selectedDesigner.portfolio_url ? (
                  <a 
                    href={selectedDesigner.portfolio_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center text-sm"
                  >
                    <ExternalLink className="mr-1 h-3 w-3" />
                    {selectedDesigner.portfolio_url}
                  </a>
                ) : (
                  <p className="text-sm text-gray-600">No portfolio provided</p>
                )}
              </div>
              <div>
                <Label className="font-semibold">Address</Label>
                <p className="text-sm text-gray-600">{selectedDesigner.address || 'Not provided'}</p>
              </div>
              {selectedDesigner.notes && (
                <div>
                  <Label className="font-semibold">Notes</Label>
                  <p className="text-sm text-gray-600">{selectedDesigner.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Close
            </Button>
            <Button 
              onClick={() => {
                setIsViewDialogOpen(false);
                handleEditDesigner(selectedDesigner);
              }}
              className="bg-brand-600 hover:bg-brand-700"
            >
              Edit Designer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Designer Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Designer</DialogTitle>
            <DialogDescription>
              Update information for {editDesigner?.name}
            </DialogDescription>
          </DialogHeader>
          {editDesigner && (
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={editDesigner.name}
                  onChange={(e) => setEditDesigner({...editDesigner, name: e.target.value})}
                  placeholder="Sarah Garden Designs"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editDesigner.email}
                  onChange={(e) => setEditDesigner({...editDesigner, email: e.target.value})}
                  placeholder="sarah@gardendesigns.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <PhoneInput
                  id="edit-phone"
                  value={editDesigner.phone}
                  onChange={(val) => setEditDesigner({...editDesigner, phone: val})}
                  placeholder="916.232.2332"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-specialty">Specialty</Label>
                <Select 
                  value={editDesigner.specialty} 
                  onValueChange={(value) => setEditDesigner({...editDesigner, specialty: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="garden_design">Garden Design</SelectItem>
                    <SelectItem value="hardscape_design">Hardscape Design</SelectItem>
                    <SelectItem value="landscape_architecture">Landscape Architecture</SelectItem>
                    <SelectItem value="sustainable_design">Sustainable Design</SelectItem>
                    <SelectItem value="commercial_design">Commercial Design</SelectItem>
                    <SelectItem value="residential_design">Residential Design</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-rate">Hourly Rate</Label>
                <Input
                  id="edit-rate"
                  type="number"
                  value={editDesigner.rate_per_hour}
                  onChange={(e) => setEditDesigner({...editDesigner, rate_per_hour: e.target.value})}
                  placeholder="150"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-his-license">HIS License Number</Label>
                <Input
                  id="edit-his-license"
                  value={editDesigner.his_license_number}
                  onChange={(e) => setEditDesigner({...editDesigner, his_license_number: e.target.value})}
                  placeholder="HIS-12345"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-portfolio">Portfolio URL</Label>
                <Input
                  id="edit-portfolio"
                  value={editDesigner.portfolio_url}
                  onChange={(e) => setEditDesigner({...editDesigner, portfolio_url: e.target.value})}
                  placeholder="https://portfolio.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={editDesigner.address}
                  onChange={(e) => setEditDesigner({...editDesigner, address: e.target.value})}
                  placeholder="123 Design St, City, State"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={editDesigner.notes}
                  onChange={(e) => setEditDesigner({...editDesigner, notes: e.target.value})}
                  placeholder="Additional notes about this designer..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateDesigner}
              disabled={updateDesignerMutation.isPending || !editDesigner?.name}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {updateDesignerMutation.isPending ? "Updating..." : "Update Designer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search designers by name, specialty, or contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Designers Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Designers ({designers.length})</CardTitle>
          <CardDescription>
            Manage your network of landscape designers and architects
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingCard>Loading designers...</LoadingCard>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="md:hidden space-y-3">
                {designers.map((designer) => (
                  <div key={designer._id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium flex items-center">
                          <Palette className="mr-2 h-4 w-4 text-purple-600 shrink-0" />
                          {designer.name}
                        </div>
                        <p className="text-sm text-gray-600 ml-6">
                          {designer.specialty ?
                            designer.specialty.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
                            : 'General Design'
                          }
                        </p>
                      </div>
                      <div className="flex shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => handleViewDesigner(designer)} className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEditDesigner(designer)} className="h-8 w-8 p-0">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteDesigner(designer)} className={`h-8 w-8 p-0 ${(deleteClicks[designer.id || designer._id] || 0) > 0 ? "text-white bg-red-600 hover:bg-red-700" : "text-red-600 hover:text-red-700"}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 space-y-1 ml-6">
                      {designer.email && (
                        <div className="text-sm flex items-center text-gray-600">
                          <Mail className="mr-1.5 h-3 w-3 shrink-0" />
                          <span className="truncate">{designer.email}</span>
                        </div>
                      )}
                      {designer.phone && (
                        <div className="text-sm flex items-center text-gray-600">
                          <Phone className="mr-1.5 h-3 w-3 shrink-0" />
                          <span>{formatPhone(designer.phone)}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap ml-6">
                      {designer.rate_per_hour ? (
                        <span className="text-sm text-green-600 font-medium flex items-center">
                          <DollarSign className="mr-0.5 h-3 w-3" />
                          {designer.rate_per_hour}/hr
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">Rate not set</span>
                      )}
                      <Badge className={designer.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}>
                        {designer.status || 'active'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table view */}
              <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name & Specialty</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Portfolio</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {designers.map((designer) => (
                  <TableRow key={designer._id}>
                    <TableCell>
                      <div>
                        <div className="font-medium flex items-center">
                          <Palette className="mr-2 h-4 w-4 text-purple-600" />
                          {designer.name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {designer.specialty ? 
                            designer.specialty.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                            : 'General Design'
                          }
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {designer.email && (
                          <div className="text-sm flex items-center">
                            <Mail className="mr-1 h-3 w-3" />
                            {designer.email}
                          </div>
                        )}
                        {designer.phone && (
                          <div className="text-sm flex items-center">
                            <Phone className="mr-1 h-3 w-3" />
                            {formatPhone(designer.phone)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {designer.rate_per_hour ? (
                        <div className="flex items-center text-green-600 font-medium">
                          <DollarSign className="mr-1 h-4 w-4" />
                          {designer.rate_per_hour}/hr
                        </div>
                      ) : (
                        <span className="text-gray-400">Not set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {designer.portfolio_url ? (
                        <a 
                          href={designer.portfolio_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline flex items-center"
                        >
                          <ExternalLink className="mr-1 h-3 w-3" />
                          Portfolio
                        </a>
                      ) : (
                        <span className="text-gray-400">No portfolio</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={designer.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}>
                        {designer.status || 'active'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleViewDesigner(designer)}
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEditDesigner(designer)}
                          title="Edit designer"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleDeleteDesigner(designer)}
                          className={`${
                            (deleteClicks[designer.id || designer._id] || 0) > 0 
                              ? "text-white bg-red-600 hover:bg-red-700" 
                              : "text-red-600 hover:text-red-700 hover:bg-red-50"
                          }`}
                          title={
                            (deleteClicks[designer.id || designer._id] || 0) > 0 
                              ? "Click again to confirm deletion" 
                              : "Delete designer"
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}